import React, { useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import {
  Activity,
  BarChart3,
  Bitcoin,
  ChevronDown,
  CircleDollarSign,
  DollarSign,
  Droplets,
  ExternalLink,
  Gem,
  Globe2,
  Landmark,
  LocateFixed,
  MapPin,
  Minus,
  Pause,
  Play,
  Plus,
  Radio,
  RefreshCw,
  Search,
  Ship,
  Shield,
  TrendingUp,
} from "lucide-react";

const API_BASE = window.location.hostname === "localhost" ? "http://localhost:5000" : "";

const C = {
  gold: "#f7c948",
  softGold: "#ffd86b",
  dimGold: "#9f7a24",
  dark: "#020202",
  panel: "#070604",
  card: "#0b0905",
  line: "rgba(247,201,72,0.26)",
  green: "#22c55e",
  emerald: "#006b49",
  red: "#ef4444",
  maroon: "#4a0712",
};

const CATEGORIES = [
  { id: "ALL", label: "All", icon: <Globe2 size={16} /> },
  { id: "WAR LIVE", label: "War Live", icon: <Shield size={16} /> },
  { id: "OIL / TANKERS", label: "Oil / Tankers", icon: <Ship size={16} /> },
  { id: "MARKETS", label: "Markets", icon: <TrendingUp size={16} /> },
  { id: "WORLD", label: "World", icon: <Radio size={16} /> },
  { id: "INDIAN NEWS", label: "Indian News", icon: <Globe2 size={16} /> },
  { id: "INDIAN STOCK MARKET", label: "Indian Stock Market", icon: <TrendingUp size={16} /> },
];

const MAP_PLACES = [
  { name: "New York", country: "USA", lat: 40.71, lon: -74.01, major: true, aliases: ["new york", "wall street", "manhattan"] },
  { name: "Washington", country: "USA", lat: 38.91, lon: -77.04, major: true, aliases: ["washington", "white house", "pentagon", "united states", "u.s.", "usa", "american"] },
  { name: "Los Angeles", country: "USA", lat: 34.05, lon: -118.24, aliases: ["los angeles", "california"] },
  { name: "Chicago", country: "USA", lat: 41.88, lon: -87.63, aliases: ["chicago"] },
  { name: "Toronto", country: "Canada", lat: 43.65, lon: -79.38, major: true, aliases: ["toronto", "canada", "canadian"] },
  { name: "Mexico City", country: "Mexico", lat: 19.43, lon: -99.13, aliases: ["mexico city", "mexico", "mexican"] },
  { name: "Havana", country: "Cuba", lat: 23.11, lon: -82.37, aliases: ["havana", "cuba", "cuban"] },
  { name: "Panama City", country: "Panama", lat: 8.98, lon: -79.52, aliases: ["panama"] },
  { name: "Bogota", country: "Colombia", lat: 4.71, lon: -74.07, aliases: ["bogota", "colombia", "colombian"] },
  { name: "Caracas", country: "Venezuela", lat: 10.48, lon: -66.9, aliases: ["caracas", "venezuela", "venezuelan"] },
  { name: "Lima", country: "Peru", lat: -12.05, lon: -77.04, aliases: ["lima", "peru", "peruvian"] },
  { name: "Sao Paulo", country: "Brazil", lat: -23.55, lon: -46.63, major: true, aliases: ["sao paulo", "brazil", "brazilian"] },
  { name: "Buenos Aires", country: "Argentina", lat: -34.6, lon: -58.38, aliases: ["buenos aires", "argentina", "argentine"] },
  { name: "Santiago", country: "Chile", lat: -33.45, lon: -70.67, aliases: ["santiago", "chile", "chilean"] },
  { name: "London", country: "UK", lat: 51.51, lon: -0.13, major: true, aliases: ["london", "britain", "british", "united kingdom", "u.k."] },
  { name: "Paris", country: "France", lat: 48.86, lon: 2.35, major: true, aliases: ["paris", "france", "french"] },
  { name: "Berlin", country: "Germany", lat: 52.52, lon: 13.41, major: true, aliases: ["berlin", "germany", "german"] },
  { name: "Brussels", country: "Belgium", lat: 50.85, lon: 4.35, aliases: ["brussels", "belgium", "european union", "eu "] },
  { name: "Amsterdam", country: "Netherlands", lat: 52.37, lon: 4.9, aliases: ["amsterdam", "netherlands", "dutch"] },
  { name: "Madrid", country: "Spain", lat: 40.42, lon: -3.7, aliases: ["madrid", "spain", "spanish"] },
  { name: "Rome", country: "Italy", lat: 41.9, lon: 12.5, aliases: ["rome", "italy", "italian"] },
  { name: "Zurich", country: "Switzerland", lat: 47.38, lon: 8.54, aliases: ["zurich", "switzerland", "swiss"] },
  { name: "Vienna", country: "Austria", lat: 48.21, lon: 16.37, aliases: ["vienna", "austria", "austrian"] },
  { name: "Warsaw", country: "Poland", lat: 52.23, lon: 21.01, aliases: ["warsaw", "poland", "polish"] },
  { name: "Kyiv", country: "Ukraine", lat: 50.45, lon: 30.52, major: true, aliases: ["kyiv", "kiev", "ukraine", "ukrainian"] },
  { name: "Moscow", country: "Russia", lat: 55.76, lon: 37.62, major: true, aliases: ["moscow", "kremlin", "russia", "russian"] },
  { name: "Oslo", country: "Norway", lat: 59.91, lon: 10.75, aliases: ["oslo", "norway", "norwegian"] },
  { name: "Stockholm", country: "Sweden", lat: 59.33, lon: 18.07, aliases: ["stockholm", "sweden", "swedish"] },
  { name: "Helsinki", country: "Finland", lat: 60.17, lon: 24.94, aliases: ["helsinki", "finland", "finnish"] },
  { name: "Istanbul", country: "Turkey", lat: 41.01, lon: 28.98, major: true, aliases: ["istanbul", "ankara", "turkey", "turkish"] },
  { name: "Cairo", country: "Egypt", lat: 30.04, lon: 31.24, major: true, aliases: ["cairo", "egypt", "egyptian"] },
  { name: "Lagos", country: "Nigeria", lat: 6.52, lon: 3.38, major: true, aliases: ["lagos", "abuja", "nigeria", "nigerian"] },
  { name: "Nairobi", country: "Kenya", lat: -1.29, lon: 36.82, aliases: ["nairobi", "kenya", "kenyan"] },
  { name: "Addis Ababa", country: "Ethiopia", lat: 9.03, lon: 38.74, aliases: ["addis ababa", "ethiopia", "ethiopian"] },
  { name: "Johannesburg", country: "South Africa", lat: -26.2, lon: 28.05, major: true, aliases: ["johannesburg", "cape town", "south africa"] },
  { name: "Riyadh", country: "Saudi Arabia", lat: 24.71, lon: 46.68, major: true, aliases: ["riyadh", "saudi arabia", "saudi"] },
  { name: "Dubai", country: "UAE", lat: 25.2, lon: 55.27, major: true, aliases: ["dubai", "abu dhabi", "united arab emirates", "uae"] },
  { name: "Tehran", country: "Iran", lat: 35.69, lon: 51.39, major: true, aliases: ["tehran", "iran", "iranian"] },
  { name: "Baghdad", country: "Iraq", lat: 33.31, lon: 44.37, aliases: ["baghdad", "iraq", "iraqi"] },
  { name: "Tel Aviv", country: "Israel", lat: 32.09, lon: 34.78, major: true, aliases: ["tel aviv", "jerusalem", "israel", "israeli"] },
  { name: "Gaza", country: "Palestine", lat: 31.5, lon: 34.47, major: true, aliases: ["gaza", "palestine", "palestinian", "west bank"] },
  { name: "Doha", country: "Qatar", lat: 25.29, lon: 51.53, aliases: ["doha", "qatar", "qatari"] },
  { name: "New Delhi", country: "India", lat: 28.61, lon: 77.21, major: true, aliases: ["new delhi", "delhi", "india", "indian", "rbi", "sebi"] },
  { name: "Mumbai", country: "India", lat: 19.08, lon: 72.88, major: true, aliases: ["mumbai", "bombay", "dalal street", "nifty", "sensex", "bse", "nse"] },
  { name: "Bengaluru", country: "India", lat: 12.97, lon: 77.59, aliases: ["bengaluru", "bangalore"] },
  { name: "Karachi", country: "Pakistan", lat: 24.86, lon: 67.01, aliases: ["karachi", "islamabad", "pakistan", "pakistani"] },
  { name: "Dhaka", country: "Bangladesh", lat: 23.81, lon: 90.41, aliases: ["dhaka", "bangladesh", "bangladeshi"] },
  { name: "Colombo", country: "Sri Lanka", lat: 6.93, lon: 79.86, aliases: ["colombo", "sri lanka"] },
  { name: "Beijing", country: "China", lat: 39.9, lon: 116.41, major: true, aliases: ["beijing", "china", "chinese"] },
  { name: "Shanghai", country: "China", lat: 31.23, lon: 121.47, major: true, aliases: ["shanghai"] },
  { name: "Hong Kong", country: "China", lat: 22.32, lon: 114.17, major: true, aliases: ["hong kong"] },
  { name: "Taipei", country: "Taiwan", lat: 25.03, lon: 121.57, major: true, aliases: ["taipei", "taiwan", "taiwanese"] },
  { name: "Tokyo", country: "Japan", lat: 35.68, lon: 139.69, major: true, aliases: ["tokyo", "japan", "japanese"] },
  { name: "Seoul", country: "South Korea", lat: 37.57, lon: 126.98, major: true, aliases: ["seoul", "south korea", "korean"] },
  { name: "Pyongyang", country: "North Korea", lat: 39.04, lon: 125.76, aliases: ["pyongyang", "north korea"] },
  { name: "Bangkok", country: "Thailand", lat: 13.76, lon: 100.5, aliases: ["bangkok", "thailand", "thai"] },
  { name: "Hanoi", country: "Vietnam", lat: 21.03, lon: 105.85, aliases: ["hanoi", "vietnam", "vietnamese"] },
  { name: "Singapore", country: "Singapore", lat: 1.35, lon: 103.82, major: true, aliases: ["singapore"] },
  { name: "Kuala Lumpur", country: "Malaysia", lat: 3.14, lon: 101.69, aliases: ["kuala lumpur", "malaysia", "malaysian"] },
  { name: "Jakarta", country: "Indonesia", lat: -6.21, lon: 106.85, major: true, aliases: ["jakarta", "indonesia", "indonesian"] },
  { name: "Manila", country: "Philippines", lat: 14.6, lon: 120.98, aliases: ["manila", "philippines", "filipino"] },
  { name: "Sydney", country: "Australia", lat: -33.87, lon: 151.21, major: true, aliases: ["sydney", "australia", "australian"] },
  { name: "Auckland", country: "New Zealand", lat: -36.85, lon: 174.76, aliases: ["auckland", "new zealand"] },
];

const NEWS_MATCH_PLACES = [...MAP_PLACES].sort(
  (a, b) => Math.max(...b.aliases.map(alias => alias.length)) - Math.max(...a.aliases.map(alias => alias.length))
);

function textHasAlias(text, alias) {
  const escaped = alias.trim().replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`(^|[^a-z])${escaped}([^a-z]|$)`, "i").test(text);
}

function locateNews(item) {
  const lat = Number(item.latitude ?? item.lat);
  const lon = Number(item.longitude ?? item.lon ?? item.lng);
  if (Number.isFinite(lat) && Number.isFinite(lon)) {
    return { name: item.location || item.city || "News location", country: item.country || "", lat, lon };
  }

  const text = `${item.title || ""} ${item.summary || ""} ${item.region || ""}`;
  const place = NEWS_MATCH_PLACES.find(candidate =>
    candidate.aliases.some(alias => textHasAlias(text, alias))
  );

  if (place) return place;
  if (item.category === "INDIAN NEWS" || item.category === "INDIAN STOCK MARKET") {
    return MAP_PLACES.find(placeItem => placeItem.name === "New Delhi");
  }
  return null;
}

function latLonToVector3(lat, lon, radius = 1) {
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (lon + 180) * (Math.PI / 180);
  return new THREE.Vector3(
    -radius * Math.sin(phi) * Math.cos(theta),
    radius * Math.cos(phi),
    radius * Math.sin(phi) * Math.sin(theta)
  );
}

function makeLabelTexture(text, color = "#f7d66b") {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  const ratio = Math.min(window.devicePixelRatio || 1, 2);
  canvas.width = 320 * ratio;
  canvas.height = 64 * ratio;
  context.scale(ratio, ratio);
  context.font = "600 20px Arial";
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.shadowColor = "rgba(0,0,0,0.95)";
  context.shadowBlur = 7;
  context.lineWidth = 5;
  context.strokeStyle = "rgba(0,0,0,0.92)";
  context.strokeText(text, 160, 32);
  context.fillStyle = color;
  context.fillText(text, 160, 32);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

function LiveWorldMap({ items }) {
  const mountRef = useRef(null);
  const globeRef = useRef(null);
  const cameraRef = useRef(null);
  const markerGroupRef = useRef(null);
  const markerMeshesRef = useRef([]);
  const labelSpritesRef = useRef([]);
  const zoomRef = useRef(3.1);
  const [query, setQuery] = useState("");
  const [mapMessage, setMapMessage] = useState("");
  const [hovered, setHovered] = useState(null);
  const [selected, setSelected] = useState(null);
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });
  const [feedOpen, setFeedOpen] = useState(false);
  const [mapFeedCategory, setMapFeedCategory] = useState("ALL");
  const [mapFeedSearch, setMapFeedSearch] = useState("");

  const newsLocations = useMemo(() => {
    const grouped = new Map();
    items.forEach(item => {
      const place = locateNews(item);
      if (!place) return;
      const key = `${place.lat.toFixed(2)}:${place.lon.toFixed(2)}`;
      if (!grouped.has(key)) grouped.set(key, { ...place, items: [] });
      grouped.get(key).items.push(item);
    });
    return [...grouped.values()].map(location => ({
      ...location,
      items: [...location.items].sort((a, b) =>
        Number(Boolean(b.marketSignal)) - Number(Boolean(a.marketSignal)) ||
        Number(Boolean(b.urgent)) - Number(Boolean(a.urgent)) ||
        Number(b.score || 0) - Number(a.score || 0)
      ),
    }));
  }, [items]);

  const oilTankerItems = useMemo(() => {
    const oilTerms = [
      "oil", "crude", "tanker", "shipping", "opec", "refinery", "petroleum",
      "lng", "natural gas", "barrel", "hormuz", "red sea", "energy",
    ];
    const direct = items.filter(item => item.category === "OIL / TANKERS");
    const related = items.filter(item => {
      const text = `${item.title} ${item.summary} ${item.source}`.toLowerCase();
      return oilTerms.some(term => text.includes(term));
    });
    const unique = new Map();
    [...direct, ...related].forEach(item => unique.set(item.id || item.link || item.title, item));
    return [...unique.values()]
      .sort((a, b) => Number(b.score || 0) - Number(a.score || 0))
      .slice(0, 5);
  }, [items]);

  const tankerDisplayItems = useMemo(() => {
    if (oilTankerItems.length) return oilTankerItems;
    return Array.from({ length: 3 }, (_, index) => ({
      id: `tanker-scanning-${index}`,
      title: "Scanning today's sources for oil and tanker intelligence",
      isPlaceholder: true,
      score: 0,
    }));
  }, [oilTankerItems]);

  const mapFeedItems = useMemo(() => {
    const needle = mapFeedSearch.trim().toLowerCase();
    return items
      .filter(item => mapFeedCategory === "ALL" || item.category === mapFeedCategory)
      .filter(item => !needle || `${item.title} ${item.summary} ${item.source}`.toLowerCase().includes(needle))
      .sort((a, b) => Number(b.score || 0) - Number(a.score || 0))
      .slice(0, 30);
  }, [items, mapFeedCategory, mapFeedSearch]);

  const focusLocation = (place, zoom = 2.05) => {
    if (!globeRef.current || !cameraRef.current) return;
    const localDirection = latLonToVector3(place.lat, place.lon).normalize();
    const targetQuaternion = new THREE.Quaternion().setFromUnitVectors(
      localDirection,
      new THREE.Vector3(0, 0, 1)
    );
    globeRef.current.quaternion.copy(targetQuaternion);
    zoomRef.current = zoom;
    cameraRef.current.position.z = zoom;
    setMapMessage(`${place.name}${place.country ? `, ${place.country}` : ""}`);
  };

  const changeZoom = amount => {
    const next = THREE.MathUtils.clamp(zoomRef.current + amount, 1.48, 4.4);
    zoomRef.current = next;
    if (cameraRef.current) cameraRef.current.position.z = next;
  };

  const searchLocation = async event => {
    event.preventDefault();
    const value = query.trim();
    if (!value) return;
    const local = MAP_PLACES.find(place =>
      `${place.name} ${place.country} ${place.aliases.join(" ")}`.toLowerCase().includes(value.toLowerCase())
    );
    if (local) {
      focusLocation(local, 1.85);
      return;
    }

    setMapMessage("Locating...");
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=1&q=${encodeURIComponent(value)}`,
        { headers: { "Accept-Language": "en" } }
      );
      const results = await response.json();
      if (!results.length) throw new Error("Place not found");
      focusLocation({
        name: results[0].display_name.split(",")[0],
        country: results[0].display_name.split(",").slice(-1)[0].trim(),
        lat: Number(results[0].lat),
        lon: Number(results[0].lon),
      }, 1.72);
    } catch (error) {
      setMapMessage(error.message || "Place not found");
    }
  };

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return undefined;

    const scene = new THREE.Scene();
    scene.fog = new THREE.FogExp2("#010307", 0.09);
    const camera = new THREE.PerspectiveCamera(42, 1, 0.1, 100);
    camera.position.set(0, 0, zoomRef.current);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setClearColor(0x010307, 1);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    mount.appendChild(renderer.domElement);

    const globe = new THREE.Group();
    globe.rotation.set(0.13, -0.55, 0);
    globeRef.current = globe;
    scene.add(globe);

    const earth = new THREE.Mesh(
      new THREE.SphereGeometry(1, 96, 96),
      new THREE.MeshPhongMaterial({
        color: 0x03111d,
        emissive: 0x02080e,
        specular: 0x1a8fb0,
        shininess: 26,
        transparent: true,
        opacity: 0.98,
      })
    );
    globe.add(earth);

    const atmosphere = new THREE.Mesh(
      new THREE.SphereGeometry(1.055, 64, 64),
      new THREE.MeshBasicMaterial({
        color: 0x178fb3,
        side: THREE.BackSide,
        transparent: true,
        opacity: 0.12,
        blending: THREE.AdditiveBlending,
      })
    );
    globe.add(atmosphere);

    const gridMaterial = new THREE.LineBasicMaterial({
      color: 0x15556b,
      transparent: true,
      opacity: 0.26,
    });
    for (let lat = -75; lat <= 75; lat += 15) {
      const points = [];
      for (let lon = -180; lon <= 180; lon += 3) points.push(latLonToVector3(lat, lon, 1.004));
      globe.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(points), gridMaterial));
    }
    for (let lon = -180; lon < 180; lon += 15) {
      const points = [];
      for (let lat = -90; lat <= 90; lat += 3) points.push(latLonToVector3(lat, lon, 1.004));
      globe.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(points), gridMaterial));
    }

    let disposed = false;
    const countryLines = new THREE.Group();
    globe.add(countryLines);
    fetch("https://raw.githubusercontent.com/nvkelso/natural-earth-vector/master/geojson/ne_110m_admin_0_countries.geojson")
      .then(response => {
        if (!response.ok) throw new Error("Country map unavailable");
        return response.json();
      })
      .then(data => {
        if (disposed) return;
        const borderMaterial = new THREE.LineBasicMaterial({
          color: 0x49afc8,
          transparent: true,
          opacity: 0.62,
        });
        const addRing = ring => {
          if (!Array.isArray(ring) || ring.length < 2) return;
          const points = ring.map(([lon, lat]) => latLonToVector3(lat, lon, 1.009));
          countryLines.add(new THREE.Line(new THREE.BufferGeometry().setFromPoints(points), borderMaterial));
        };
        (data.features || []).forEach(feature => {
          const { type, coordinates } = feature.geometry || {};
          if (type === "Polygon") coordinates.forEach(addRing);
          if (type === "MultiPolygon") coordinates.forEach(polygon => polygon.forEach(addRing));
        });
      })
      .catch(() => {
        // The globe remains fully usable with its latitude/longitude grid when offline.
      });

    const markerGroup = new THREE.Group();
    markerGroupRef.current = markerGroup;
    globe.add(markerGroup);

    const cityMaterial = new THREE.MeshBasicMaterial({ color: 0xf2c94c });
    MAP_PLACES.forEach(place => {
      const dot = new THREE.Mesh(
        new THREE.SphereGeometry(place.major ? 0.009 : 0.0055, 8, 8),
        cityMaterial
      );
      dot.position.copy(latLonToVector3(place.lat, place.lon, 1.012));
      dot.userData.place = place;
      globe.add(dot);

      const material = new THREE.SpriteMaterial({
        map: makeLabelTexture(place.name, place.major ? "#ffe18a" : "#9eb4ba"),
        transparent: true,
        depthTest: false,
        opacity: place.major ? 0.92 : 0.72,
      });
      const label = new THREE.Sprite(material);
      label.position.copy(latLonToVector3(place.lat, place.lon, 1.045));
      label.scale.set(place.major ? 0.33 : 0.26, place.major ? 0.066 : 0.052, 1);
      label.userData = { major: Boolean(place.major) };
      labelSpritesRef.current.push(label);
      globe.add(label);
    });

    scene.add(new THREE.AmbientLight(0x6a91a3, 1.45));
    const sunlight = new THREE.DirectionalLight(0xb6e8ff, 2.2);
    sunlight.position.set(-3, 2, 4);
    scene.add(sunlight);
    const rimLight = new THREE.DirectionalLight(0x0a6687, 2.4);
    rimLight.position.set(4, -1, -3);
    scene.add(rimLight);

    const stars = new Float32Array(2400);
    for (let index = 0; index < stars.length; index += 3) {
      const radius = 4 + Math.random() * 9;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      stars[index] = radius * Math.sin(phi) * Math.cos(theta);
      stars[index + 1] = radius * Math.cos(phi);
      stars[index + 2] = radius * Math.sin(phi) * Math.sin(theta);
    }
    const starGeometry = new THREE.BufferGeometry();
    starGeometry.setAttribute("position", new THREE.BufferAttribute(stars, 3));
    scene.add(new THREE.Points(
      starGeometry,
      new THREE.PointsMaterial({ color: 0x7eb7ca, size: 0.014, transparent: true, opacity: 0.68 })
    ));

    let dragging = false;
    let moved = false;
    let pointer = { x: 0, y: 0 };
    let lastHoveredId = "";
    let hoverSnapshot = null;
    let hoverOrigin = null;
    let transitionQuaternion = null;
    let transitionZoom = null;
    let restoringWorld = false;
    let manuallyPositioned = false;
    let pressedNews = null;
    const newsIdentity = news => String(news?.id || news?.link || news?.title || "");
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();

    const transitionToLocation = location => {
      restoringWorld = false;
      if (!hoverSnapshot) {
        hoverSnapshot = {
          quaternion: globe.quaternion.clone(),
          zoom: camera.position.z,
        };
      }
      transitionQuaternion = new THREE.Quaternion().setFromUnitVectors(
        latLonToVector3(location.lat, location.lon).normalize(),
        new THREE.Vector3(0, 0, 1)
      );
      transitionZoom = 1.68;
      setMapMessage(`${location.name}${location.country ? `, ${location.country}` : ""}`);
    };

    const restoreWorldView = () => {
      if (hoverSnapshot) {
        transitionQuaternion = hoverSnapshot.quaternion.clone();
        transitionZoom = hoverSnapshot.zoom;
        restoringWorld = true;
      }
      hoverSnapshot = null;
      hoverOrigin = null;
      setMapMessage("");
    };

    const resize = () => {
      const width = Math.max(mount.clientWidth, 1);
      const height = Math.max(mount.clientHeight, 1);
      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
    };
    const observer = new ResizeObserver(resize);
    observer.observe(mount);
    resize();

    const onPointerDown = event => {
      pressedNews = markerMeshesRef.current.find(
        mesh => newsIdentity(mesh.userData.news) === lastHoveredId
      )?.userData.news || null;
      transitionQuaternion = null;
      transitionZoom = null;
      restoringWorld = false;
      hoverSnapshot = null;
      hoverOrigin = null;
      lastHoveredId = "";
      setHovered(null);
      setMapMessage("");
      dragging = true;
      moved = false;
      manuallyPositioned = true;
      pointer = { x: event.clientX, y: event.clientY };
      renderer.domElement.style.cursor = "grabbing";
      renderer.domElement.setPointerCapture?.(event.pointerId);
    };
    const onPointerMove = event => {
      if (dragging) {
        const dx = event.clientX - pointer.x;
        const dy = event.clientY - pointer.y;
        if (Math.abs(dx) + Math.abs(dy) > 2) moved = true;
        globe.rotation.y += dx * 0.005;
        globe.rotation.x = THREE.MathUtils.clamp(globe.rotation.x + dy * 0.004, -1.25, 1.25);
        pointer = { x: event.clientX, y: event.clientY };
        return;
      }
      const bounds = renderer.domElement.getBoundingClientRect();
      const localPointer = {
        x: event.clientX - bounds.left,
        y: event.clientY - bounds.top,
      };

      if (restoringWorld) {
        renderer.domElement.style.cursor = "grab";
        return;
      }

      if (lastHoveredId && hoverOrigin) {
        const distance = Math.hypot(
          localPointer.x - hoverOrigin.x,
          localPointer.y - hoverOrigin.y
        );

        if (distance > 14) {
          restoreWorldView();
          lastHoveredId = "";
          setHovered(null);
          renderer.domElement.style.cursor = "grab";
        } else {
          setHoverPosition(localPointer);
        }
        return;
      }

      mouse.x = ((event.clientX - bounds.left) / bounds.width) * 2 - 1;
      mouse.y = -((event.clientY - bounds.top) / bounds.height) * 2 + 1;
      raycaster.setFromCamera(mouse, camera);
      const hits = raycaster.intersectObjects(markerMeshesRef.current, false);
      const hit = hits.find(result => result.object.userData.highImpact) || hits[0];
      const news = hit?.object.userData.news || null;
      const location = hit?.object.userData.location || null;
      const id = newsIdentity(news);
      if (id !== lastHoveredId) {
        if (news && location) {
          hoverOrigin = localPointer;
          transitionToLocation(location);
        }
        else if (lastHoveredId) restoreWorldView();
        lastHoveredId = id;
        setHovered(news);
      }
      setHoverPosition(localPointer);
      renderer.domElement.style.cursor = news ? "pointer" : "grab";
    };
    const onPointerUp = () => {
      if (!moved && pressedNews) setSelected(pressedNews);
      pressedNews = null;
      dragging = false;
      renderer.domElement.style.cursor = "grab";
    };
    const onWheel = event => {
      event.preventDefault();
      changeZoom(event.deltaY * 0.0014);
    };
    const onLeave = () => {
      dragging = false;
      if (lastHoveredId) restoreWorldView();
      lastHoveredId = "";
      hoverOrigin = null;
      setHovered(null);
    };

    renderer.domElement.addEventListener("pointerdown", onPointerDown);
    renderer.domElement.addEventListener("pointermove", onPointerMove);
    renderer.domElement.addEventListener("pointerup", onPointerUp);
    renderer.domElement.addEventListener("pointerleave", onLeave);
    renderer.domElement.addEventListener("wheel", onWheel, { passive: false });

    let animationFrame;
    const clock = new THREE.Clock();
    const animate = () => {
      const elapsed = clock.getElapsedTime();
      if (transitionQuaternion && transitionZoom !== null) {
        globe.quaternion.slerp(transitionQuaternion, 0.09);
        camera.position.z = THREE.MathUtils.lerp(camera.position.z, transitionZoom, 0.09);
        zoomRef.current = camera.position.z;

        if (
          globe.quaternion.angleTo(transitionQuaternion) < 0.002 &&
          Math.abs(camera.position.z - transitionZoom) < 0.003
        ) {
          globe.quaternion.copy(transitionQuaternion);
          camera.position.z = transitionZoom;
          zoomRef.current = transitionZoom;
          transitionQuaternion = null;
          transitionZoom = null;
          restoringWorld = false;
        }
      } else if (!dragging && !lastHoveredId && !manuallyPositioned) {
        globe.rotation.y += 0.00045;
      }
      markerMeshesRef.current.forEach((marker, index) => {
        const pulse = 1 + Math.sin(elapsed * 4.2 + index * 0.75) * 0.34;
        if (marker.userData.highImpact) {
          marker.scale.set(0.42 * pulse, 0.14 * pulse, 1);
        } else {
          marker.scale.setScalar(pulse);
        }
        marker.material.opacity = 0.7 + Math.sin(elapsed * 4.2 + index) * 0.25;
      });
      labelSpritesRef.current.forEach(label => {
        const worldPosition = new THREE.Vector3();
        label.getWorldPosition(worldPosition);
        const facing = worldPosition.clone().normalize().dot(camera.position.clone().sub(worldPosition).normalize());
        label.visible = facing > 0.05 && (label.userData.major || camera.position.z < 2.45);
      });
      renderer.render(scene, camera);
      animationFrame = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      disposed = true;
      cancelAnimationFrame(animationFrame);
      observer.disconnect();
      renderer.domElement.removeEventListener("pointerdown", onPointerDown);
      renderer.domElement.removeEventListener("pointermove", onPointerMove);
      renderer.domElement.removeEventListener("pointerup", onPointerUp);
      renderer.domElement.removeEventListener("pointerleave", onLeave);
      renderer.domElement.removeEventListener("wheel", onWheel);
      scene.traverse(object => {
        object.geometry?.dispose();
        if (Array.isArray(object.material)) object.material.forEach(material => material.dispose());
        else {
          object.material?.map?.dispose();
          object.material?.dispose();
        }
      });
      renderer.dispose();
      mount.removeChild(renderer.domElement);
      globeRef.current = null;
      cameraRef.current = null;
      markerGroupRef.current = null;
      markerMeshesRef.current = [];
      labelSpritesRef.current = [];
    };
  }, []);

  useEffect(() => {
    const group = markerGroupRef.current;
    if (!group) return;
    group.children.forEach(child => {
      child.geometry?.dispose();
      child.material?.dispose();
    });
    group.clear();
    markerMeshesRef.current = [];

    newsLocations.forEach((location, index) => {
      const primary = location.items[0];
      const highImpactNews = [...location.items]
        .filter(item => Number(item.score || 0) >= 75)
        .sort((a, b) => Number(b.score || 0) - Number(a.score || 0))[0];
      const highImpact = Boolean(highImpactNews);
      const markerColor = primary.marketSignal ? 0x16e07a : (primary.urgent ? 0xff1838 : 0xff4d3d);
      const marker = new THREE.Mesh(
        new THREE.SphereGeometry(0.024 + Math.min(location.items.length, 5) * 0.003, 18, 18),
        new THREE.MeshBasicMaterial({
          color: markerColor,
          transparent: true,
          opacity: 0.95,
        })
      );
      marker.position.copy(latLonToVector3(location.lat, location.lon, 1.05));
      marker.userData = { news: primary, location };
      group.add(marker);

      const ring = new THREE.Mesh(
        new THREE.RingGeometry(0.035, 0.047, 32),
        new THREE.MeshBasicMaterial({
          color: markerColor,
          transparent: true,
          opacity: primary.urgent ? 0.8 : 0.44,
          side: THREE.DoubleSide,
          depthWrite: false,
        })
      );
      ring.position.copy(latLonToVector3(location.lat, location.lon, 1.035));
      ring.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), ring.position.clone().normalize());
      ring.userData.phase = index;
      group.add(ring);
      markerMeshesRef.current.push(marker);

      if (highImpact) {
        const star = new THREE.Sprite(new THREE.SpriteMaterial({
          map: makeLabelTexture("*", "#ffd86b"),
          transparent: true,
          depthTest: false,
        }));
        star.position.copy(latLonToVector3(location.lat, location.lon, 1.105));
        star.scale.set(0.42, 0.14, 1);
        star.userData = {
          highImpact: true,
          news: highImpactNews,
          location,
        };
        group.add(star);
        markerMeshesRef.current.push(star);
      }
    });
  }, [newsLocations]);

  return (
    <section
      className="-mx-5 overflow-hidden border-x-0 border-y"
      style={{
        borderColor: "rgba(49,151,179,0.35)",
        background: "linear-gradient(145deg, #010407, #020a10 48%, #010203)",
        boxShadow: "0 0 42px rgba(0,86,116,0.12), inset 0 0 60px rgba(0,0,0,0.65)",
      }}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3" style={{ borderColor: "rgba(49,151,179,0.24)" }}>
        <div>
          <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.22em]" style={{ color: "#8ce8ff" }}>
            <Globe2 size={18} /> Live World Intelligence Map
          </div>
          <div className="mt-1 text-[10px] uppercase tracking-[0.14em]" style={{ color: "#537d89" }}>
            Hover a signal to auto-focus · Move away to restore world view · Red is news · Green is stock intelligence
          </div>
        </div>
        <div className="flex items-center gap-3 text-[10px] font-bold uppercase tracking-[0.14em]">
          <span className="flex items-center gap-2" style={{ color: "#ff6b76" }}>
            <span className="bw-live-dot" /> {newsLocations.length} active locations
          </span>
          <span className="flex items-center gap-2" style={{ color: "#37e895" }}>
            <span className="bw-stock-dot" /> {items.filter(item => item.marketSignal).length} stock signals
          </span>
          <span style={{ color: "#ffffff" }}>* High impact</span>
          <span style={{ color: "#5f8b98" }}>{items.length - newsLocations.reduce((total, location) => total + location.items.length, 0)} scanning</span>
        </div>
      </div>

      <div className="relative h-[calc(100vh-54px)] min-h-[680px] w-full overflow-hidden">
          <div ref={mountRef} className="absolute inset-0" />
          <div className="pointer-events-none absolute inset-0 bw-map-vignette" />

          <form
            onSubmit={searchLocation}
            className="absolute left-4 top-4 flex w-[min(380px,calc(100%-90px))] items-center gap-2 rounded border px-3 py-2 backdrop-blur"
            style={{ borderColor: "rgba(84,181,207,0.32)", background: "rgba(1,8,13,0.86)" }}
          >
            <LocateFixed size={16} style={{ color: "#72dbf5" }} />
            <input
              value={query}
              onChange={event => setQuery(event.target.value)}
              placeholder="Find any city or country..."
              className="min-w-0 flex-1 bg-transparent text-sm outline-none"
              style={{ color: "#d4f7ff" }}
            />
            <button type="submit" className="text-[10px] font-bold uppercase tracking-[0.12em]" style={{ color: "#72dbf5" }}>
              Locate
            </button>
          </form>

          <button
            type="button"
            onClick={() => setFeedOpen(open => !open)}
            className="absolute right-4 top-4 z-30 flex items-center gap-2 rounded border px-4 py-3 text-[10px] font-bold uppercase tracking-[0.16em] backdrop-blur"
            style={{
              borderColor: feedOpen ? "rgba(247,201,72,0.65)" : "rgba(84,181,207,0.3)",
              background: "rgba(1,8,13,0.9)",
              color: feedOpen ? C.softGold : "#9cecff",
            }}
          >
            Intelligence feed <ChevronDown size={15} style={{ transform: feedOpen ? "rotate(180deg)" : "none" }} />
          </button>

          <div className="absolute right-4 top-[68px] z-20 flex flex-col overflow-hidden rounded border" style={{ borderColor: "rgba(84,181,207,0.3)", background: "rgba(1,8,13,0.86)" }}>
            <button type="button" onClick={() => changeZoom(-0.3)} className="p-2" title="Zoom in" style={{ color: "#9cecff" }}>
              <Plus size={17} />
            </button>
            <button type="button" onClick={() => changeZoom(0.3)} className="border-t p-2" title="Zoom out" style={{ color: "#9cecff", borderColor: "rgba(84,181,207,0.25)" }}>
              <Minus size={17} />
            </button>
          </div>

          {feedOpen && (
            <div
              className="absolute bottom-[132px] right-16 top-[68px] z-20 flex w-[min(470px,calc(100%-80px))] flex-col overflow-hidden rounded border backdrop-blur-xl"
              style={{
                borderColor: "rgba(247,201,72,0.32)",
                background: "rgba(2,4,6,0.94)",
                boxShadow: "0 18px 55px rgba(0,0,0,0.55)",
              }}
            >
              <div className="border-b p-4" style={{ borderColor: "rgba(247,201,72,0.2)" }}>
                <div className="mb-3 text-xs font-bold uppercase tracking-[0.2em]" style={{ color: C.softGold }}>
                  Today&apos;s Intelligence
                </div>
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {CATEGORIES.map(filter => (
                    <button
                      key={filter.id}
                      type="button"
                      onClick={() => setMapFeedCategory(filter.id)}
                      className="flex shrink-0 items-center gap-2 rounded border px-3 py-2 text-[10px] font-bold"
                      style={{
                        color: mapFeedCategory === filter.id ? "#ffffff" : C.gold,
                        borderColor: mapFeedCategory === filter.id ? C.green : C.line,
                        background: mapFeedCategory === filter.id ? C.emerald : C.card,
                      }}
                    >
                      {filter.icon} {filter.label}
                    </button>
                  ))}
                </div>
                <div className="mt-2 flex items-center gap-2 rounded border px-3 py-2" style={{ borderColor: C.line, background: C.card }}>
                  <Search size={15} />
                  <input
                    value={mapFeedSearch}
                    onChange={event => setMapFeedSearch(event.target.value)}
                    placeholder="Search today's intelligence..."
                    className="min-w-0 flex-1 bg-transparent text-sm outline-none"
                    style={{ color: C.softGold }}
                  />
                </div>
              </div>
              <div className="min-h-0 flex-1 overflow-y-auto p-3">
                {mapFeedItems.map(item => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => {
                      const place = locateNews(item);
                      if (place) focusLocation(place, 1.82);
                      setSelected(item);
                    }}
                    className="mb-2 block w-full rounded border p-3 text-left"
                    style={{
                      borderColor: item.marketSignal ? "rgba(22,224,122,0.3)" : "rgba(255,55,75,0.24)",
                      background: item.marketSignal ? "rgba(3,24,15,0.72)" : "rgba(20,5,9,0.62)",
                    }}
                  >
                    <div className="flex items-center justify-between gap-3 text-[9px] font-bold uppercase tracking-[0.14em]">
                      <span style={{ color: item.marketSignal ? "#37e895" : "#ff6675" }}>
                        {Number(item.score || 0) >= 75 ? "* " : ""}{item.source}
                      </span>
                      <span style={{ color: "#5d7a82" }}>{timeText(item.publishedAt)}</span>
                    </div>
                    <div className="mt-2 text-sm font-bold leading-snug" style={{ color: "#e5f9ff" }}>{item.title}</div>
                  </button>
                ))}
                {!mapFeedItems.length && (
                  <div className="p-8 text-center text-xs uppercase tracking-[0.14em]" style={{ color: "#53727a" }}>
                    No matching intelligence today
                  </div>
                )}
              </div>
            </div>
          )}

          {mapMessage && (
            <div className="absolute bottom-4 left-4 rounded border px-3 py-2 text-xs font-bold uppercase tracking-[0.12em] backdrop-blur" style={{ color: "#9cecff", borderColor: "rgba(84,181,207,0.3)", background: "rgba(1,8,13,0.82)" }}>
              <MapPin size={13} className="mr-2 inline" /> {mapMessage}
            </div>
          )}

          {hovered && (
            <div
              className="pointer-events-none absolute z-20 w-[300px] max-w-[calc(100%-24px)] rounded border p-3 backdrop-blur"
              style={{
                left: Math.min(hoverPosition.x + 18, Math.max((mountRef.current?.clientWidth || 340) - 312, 12)),
                top: Math.max(Math.min(hoverPosition.y - 30, 380), 74),
                borderColor: hovered.marketSignal ? "rgba(34,224,122,0.62)" : "rgba(255,50,72,0.6)",
                background: hovered.marketSignal ? "rgba(2,12,8,0.95)" : "rgba(8,3,7,0.94)",
                boxShadow: hovered.marketSignal ? "0 0 25px rgba(34,224,122,0.18)" : "0 0 25px rgba(255,24,55,0.18)",
              }}
            >
              <div className="mb-1 text-[9px] font-bold uppercase tracking-[0.16em]" style={{ color: hovered.marketSignal ? "#37e895" : "#ff5868" }}>
                {Number(hovered.score || 0) >= 75 ? "* High impact · " : ""}
                {hovered.marketSignal ? "Stock intelligence" : "Live signal"} · {locateNews(hovered)?.name}
              </div>
              <div className="text-sm font-bold leading-snug" style={{ color: "#ffe8ea" }}>{hovered.title}</div>
              <div className="mt-2 text-[10px]" style={{ color: "#ad7c83" }}>{hovered.source} · {timeText(hovered.publishedAt)}</div>
            </div>
          )}

          <div className="absolute inset-x-0 bottom-0 z-10 h-[118px] overflow-hidden border-t bw-ocean-lane" style={{ borderColor: "rgba(33,151,184,0.32)" }}>
            <div className="absolute left-4 top-3 text-[9px] font-bold uppercase tracking-[0.2em]" style={{ color: "#62bcd3" }}>
              Live tanker traffic · {oilTankerItems.length} oil signals
            </div>
            {tankerDisplayItems.map((item, index) => (
              <button
                key={item.id}
                type="button"
                onMouseEnter={() => {
                  if (!item.isPlaceholder) setSelected(item);
                }}
                onClick={() => {
                  if (!item.isPlaceholder) setSelected(item);
                }}
                className={`bw-tanker absolute bottom-4${item.isPlaceholder ? " bw-tanker-scanning" : ""}`}
                style={{
                  "--bw-tanker-delay": `${index * -18}s`,
                  "--bw-tanker-duration": `${82 + index * 12}s`,
                }}
                title={item.title}
              >
                <span className="bw-tanker-signal">{Number(item.score || 0) >= 75 ? "*" : ""}</span>
                <span className="bw-tanker-bridge" />
                <span className="bw-tanker-deck">
                  <span /><span /><span />
                </span>
                <span className="bw-tanker-hull" />
                <span className="bw-tanker-wake" />
              </button>
            ))}
          </div>
      </div>

      <div
        className="grid gap-5 border-t p-5 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1.85fr)]"
        style={{ borderColor: "rgba(49,151,179,0.24)", background: "rgba(1,5,8,0.94)" }}
      >
        <section className="min-h-[290px] rounded border p-5" style={{ borderColor: "rgba(49,151,179,0.2)", background: "rgba(3,12,17,0.72)" }}>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: "#5c8d99" }}>
            Signal detail
          </div>
          {selected ? (
            <div className="mt-4">
              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.14em]" style={{ color: "#ff4a5e" }}>
                <span className={selected.marketSignal ? "bw-stock-dot" : "bw-live-dot"} />
                <span style={{ color: selected.marketSignal ? "#37e895" : "#ff4a5e" }}>
                  {selected.marketSignal ? "Stock intelligence" : "World news"} · {locateNews(selected)?.name || "Global"}
                </span>
              </div>
              <h3 className="mt-4 text-xl font-bold leading-snug" style={{ color: "#e5f9ff" }}>{selected.title}</h3>
              {selected.summary && <p className="mt-3 line-clamp-5 text-sm leading-relaxed" style={{ color: "#83a4ad" }}>{selected.summary}</p>}
              <div className="mt-4 border-y py-3 text-xs" style={{ color: "#6f949e", borderColor: "rgba(49,151,179,0.2)" }}>
                <div>{selected.source} · {timeText(selected.publishedAt)}</div>
                <div className="mt-1" style={{ color: scoreColor(selected.score) }}>Impact score {selected.score}/100</div>
              </div>
              <a
                href={selected.link}
                target="_blank"
                rel="noreferrer"
                className="mt-4 flex items-center justify-center gap-2 rounded px-4 py-3 text-xs font-bold uppercase tracking-[0.14em]"
                style={{
                  background: selected.marketSignal
                    ? "linear-gradient(135deg, #075b37, #0fa765)"
                    : "linear-gradient(135deg, #8c0d20, #dc1836)",
                  color: "#ffffff",
                }}
              >
                Open source <ExternalLink size={14} />
              </a>
            </div>
          ) : (
            <div className="flex min-h-[235px] flex-col items-center justify-center text-center">
              <div className="relative">
                <Globe2 size={56} strokeWidth={1} style={{ color: "#184d5d" }} />
                <span className="bw-live-dot absolute right-0 top-0" />
              </div>
              <div className="mt-5 text-sm font-bold uppercase tracking-[0.15em]" style={{ color: "#77b8c8" }}>Select a live signal</div>
              <p className="mt-2 max-w-[230px] text-xs leading-relaxed" style={{ color: "#456c76" }}>
                Hover a signal to preview its headline. Green points flag stock-sensitive information; red points show general and breaking news.
              </p>
            </div>
          )}
        </section>

        <section className="rounded border p-5" style={{ borderColor: "rgba(49,151,179,0.2)", background: "rgba(3,12,17,0.72)" }}>
          <div className="mb-4 flex items-center justify-between gap-3">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: "#5c8d99" }}>
              Latest mapped signals
            </div>
            <div className="text-[9px] font-bold uppercase tracking-[0.14em]" style={{ color: "#375d67" }}>
              Select any signal to locate
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {newsLocations.slice(0, 9).map(location => (
                <button
                  key={`${location.lat}-${location.lon}`}
                  type="button"
                  onClick={() => {
                    focusLocation(location);
                    setSelected(location.items[0]);
                  }}
                  className="flex min-h-[74px] w-full items-center gap-3 rounded border p-3 text-left transition hover:border-cyan-700"
                  style={{ borderColor: "rgba(49,151,179,0.16)", background: "rgba(3,15,21,0.7)" }}
                >
                  <span className={`${location.items[0].marketSignal ? "bw-stock-dot" : "bw-live-dot"} shrink-0`} />
                  <span className="min-w-0">
                    <span className="block text-[10px] font-bold uppercase" style={{ color: "#8fd8e9" }}>{location.name}</span>
                    <span className="mt-1 block line-clamp-2 text-[10px] leading-relaxed" style={{ color: "#557b85" }}>{location.items[0].title}</span>
                  </span>
                </button>
              ))}
          </div>
        </section>
      </div>
    </section>
  );
}

const MARKET_META = {
  BTC: {
    icon: <Bitcoin size={15} />,
    decimals: 2,
  },
  USDINR: {
    icon: <DollarSign size={15} />,
    decimals: 4,
  },
  ETH: {
    icon: <Gem size={15} />,
    decimals: 2,
  },
  XAUUSD: {
    icon: <CircleDollarSign size={15} />,
    decimals: 2,
  },
  US30: {
    icon: <Landmark size={15} />,
    decimals: 2,
  },
  SPX: {
    icon: <BarChart3 size={15} />,
    decimals: 2,
  },
  NASDAQ: {
    icon: <Activity size={15} />,
    decimals: 2,
  },
  CRUDE: {
    icon: <Droplets size={15} />,
    decimals: 2,
  },
  DXY: {
    icon: <DollarSign size={15} />,
    decimals: 3,
  },
};

function timeText(value) {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";

  return date.toLocaleString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    day: "2-digit",
    month: "short",
  });
}

function scoreColor(score) {
  if (score >= 75) return C.red;
  if (score >= 55) return C.green;
  return C.gold;
}

function MarketStrip({ items, error }) {
  if (!items.length) {
    return (
      <div
        className="text-center text-[10px] font-bold uppercase tracking-[0.16em]"
        style={{ color: error ? C.red : C.dimGold }}
      >
        {error || "Loading global markets..."}
      </div>
    );
  }

  return (
    <div className="min-w-0 overflow-x-auto">
      <div className="flex min-w-max items-center">
        {items.map(item => {
          const meta = MARKET_META[item.id] || {};
          const change = Number(item.change || 0);
          const positive = change >= 0;
          const decimals = meta.decimals ?? 2;

          return (
            <div
              key={item.id}
              className="flex h-12 min-w-[126px] items-center gap-2 border-r px-3"
              style={{ borderColor: C.line }}
              title={`${item.label} ${item.marketState || ""}`}
            >
              <div style={{ color: C.softGold }}>
                {meta.icon || <Activity size={15} />}
              </div>

              <div className="min-w-0">
                <div
                  className="truncate text-[10px] font-bold"
                  style={{ color: C.softGold }}
                >
                  {item.label}
                </div>

                <div
                  className="bw-market-price-blink whitespace-nowrap text-sm font-bold"
                  style={{
                    color: positive ? "#34d399" : "#fb7185",
                  }}
                >
                  {Number(item.price || 0).toLocaleString("en-IN", {
                    minimumFractionDigits: decimals,
                    maximumFractionDigits: decimals,
                  })}
                </div>

                <div
                  className="whitespace-nowrap text-[10px] font-bold"
                  style={{
                    color: positive ? "#22c55e" : "#ef4444",
                    textShadow: positive
                      ? "0 0 8px rgba(34,197,94,0.75)"
                      : "0 0 8px rgba(239,68,68,0.75)",
                  }}
                >
                  {positive ? "+" : ""}
                  {change.toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                  {" "}
                  ({positive ? "+" : ""}
                  {Number(item.changePercent || 0).toFixed(2)}%)
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function BloombergWatch() {
  const [items, setItems] = useState([]);
  const [updatedAt, setUpdatedAt] = useState("");
  const [error, setError] = useState("");
  const [countdown, setCountdown] = useState(5);
  const [marketQuotes, setMarketQuotes] = useState([]);
  const [marketError, setMarketError] = useState("");
  const [intelligenceEnabled, setIntelligenceEnabled] = useState(false);
  const [controlBusy, setControlBusy] = useState(false);
  const intelligenceEnabledRef = useRef(false);

  const loadNews = async () => {
    setError("");

    try {
      const params = new URLSearchParams({
        category: "ALL",
        q: "",
        t: String(Date.now()),
      });

      const res = await fetch(`${API_BASE}/api/bloomberg-news/live?${params.toString()}`, {
        cache: "no-store",
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.success) {
        throw new Error(data.error || "BloombergWatch news failed");
      }

      const enabled = data.enabled !== false;
      intelligenceEnabledRef.current = enabled;
      setIntelligenceEnabled(enabled);
      const today = new Intl.DateTimeFormat("en-CA", {
        timeZone: "Asia/Kolkata",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(new Date());
      setItems((data.items || []).filter(item => {
        const date = new Date(item.publishedAt);
        if (Number.isNaN(date.getTime())) return false;
        return new Intl.DateTimeFormat("en-CA", {
          timeZone: "Asia/Kolkata",
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        }).format(date) === today;
      }));
      setUpdatedAt(data.updatedAt || new Date().toISOString());
      setCountdown(5);
    } catch (err) {
      setError(err.message || "BloombergWatch news failed");
    }
  };

  const toggleIntelligence = async () => {
    const enabled = !intelligenceEnabledRef.current;
    setControlBusy(true);
    setError("");

    try {
      const res = await fetch(`${API_BASE}/api/bloomberg-news/control`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Unable to change intelligence status");
      }

      intelligenceEnabledRef.current = data.enabled;
      setIntelligenceEnabled(data.enabled);
      setCountdown(data.enabled ? 5 : 0);
      if (data.enabled) {
        await Promise.all([loadNews(), loadMarketQuotes()]);
      }
    } catch (err) {
      setError(err.message || "Unable to change intelligence status");
    } finally {
      setControlBusy(false);
    }
  };

  const loadMarketQuotes = async () => {
    try {
      const res = await fetch(
        `${API_BASE}/api/global-markets/quotes?t=${Date.now()}`,
        { cache: "no-store" }
      );

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.success) {
        throw new Error(data.error || "Global market prices failed");
      }

      setMarketQuotes(data.items || []);
      setMarketError("");
    } catch (err) {
      setMarketError(err.message || "Global market prices failed");
    }
  };

  useEffect(() => {
    loadNews();

    const timer = setInterval(() => {
      if (intelligenceEnabledRef.current) loadNews();
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (!intelligenceEnabledRef.current) return 0;
        return prev <= 1 ? 5 : prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    loadMarketQuotes();

    const timer = setInterval(() => {
      if (intelligenceEnabledRef.current) loadMarketQuotes();
    }, 5000);

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen p-5" style={{ background: C.dark, color: C.gold }}>
      <style>
        {`
          @keyframes bwMarketPriceBlink {
            0%, 100% {
              opacity: 1;
              text-shadow:
                0 0 5px currentColor,
                0 0 10px currentColor,
                0 0 18px currentColor;
            }

            50% {
              opacity: 0.55;
              text-shadow:
                0 0 2px currentColor,
                0 0 6px currentColor;
            }
          }

          .bw-market-price-blink {
            animation: bwMarketPriceBlink 1.2s ease-in-out infinite;
          }

          @keyframes bwLiveDot {
            0%, 100% {
              opacity: 1;
              transform: scale(1);
              box-shadow: 0 0 4px #ff1838, 0 0 12px rgba(255,24,56,0.85), 0 0 24px rgba(255,24,56,0.5);
            }
            50% {
              opacity: 0.48;
              transform: scale(0.72);
              box-shadow: 0 0 3px #ff1838, 0 0 7px rgba(255,24,56,0.5);
            }
          }

          .bw-live-dot {
            display: inline-block;
            width: 8px;
            height: 8px;
            border-radius: 999px;
            background: #ff1838;
            animation: bwLiveDot 1.25s ease-in-out infinite;
          }

          @keyframes bwStockDot {
            0%, 100% {
              opacity: 1;
              transform: scale(1);
              box-shadow: 0 0 4px #16e07a, 0 0 12px rgba(22,224,122,0.88), 0 0 24px rgba(22,224,122,0.52);
            }
            50% {
              opacity: 0.5;
              transform: scale(0.72);
              box-shadow: 0 0 3px #16e07a, 0 0 7px rgba(22,224,122,0.52);
            }
          }

          .bw-stock-dot {
            display: inline-block;
            width: 8px;
            height: 8px;
            border-radius: 999px;
            background: #16e07a;
            animation: bwStockDot 1.05s ease-in-out infinite;
          }

          .bw-map-vignette {
            background:
              radial-gradient(circle at center, transparent 42%, rgba(1,3,6,0.18) 68%, rgba(1,3,6,0.72) 100%),
              linear-gradient(180deg, rgba(0,0,0,0.15), transparent 20%, transparent 80%, rgba(0,0,0,0.34));
            box-shadow: inset 0 0 70px rgba(0,0,0,0.72);
          }

          @keyframes bwOceanDrift {
            from { background-position: 0 0, 0 0; }
            to { background-position: 180px 0, -260px 0; }
          }

          @keyframes bwTankerSail {
            from { left: -150px; }
            to { left: calc(100% + 40px); }
          }

          @keyframes bwTankerBob {
            0%, 100% { transform: translateY(0) rotate(-0.4deg); }
            50% { transform: translateY(-4px) rotate(0.5deg); }
          }

          .bw-ocean-lane {
            background:
              repeating-radial-gradient(ellipse at 50% 100%, rgba(45,188,220,0.16) 0 2px, transparent 3px 30px),
              linear-gradient(180deg, rgba(1,12,20,0.2), rgba(3,43,60,0.78));
            animation: bwOceanDrift 12s linear infinite;
            box-shadow: inset 0 18px 34px rgba(0,0,0,0.5);
          }

          .bw-tanker {
            width: 116px;
            height: 50px;
            animation:
              bwTankerSail var(--bw-tanker-duration) linear infinite var(--bw-tanker-delay),
              bwTankerBob 2.8s ease-in-out infinite;
            filter: drop-shadow(0 9px 8px rgba(0,0,0,0.55));
          }

          .bw-tanker:hover,
          .bw-tanker:focus-visible {
            animation-play-state: paused;
            filter: drop-shadow(0 9px 8px rgba(0,0,0,0.65)) drop-shadow(0 0 12px rgba(98,188,211,0.55));
            outline: none;
          }

          .bw-tanker-hull {
            position: absolute;
            left: 5px;
            bottom: 5px;
            width: 106px;
            height: 17px;
            border-radius: 3px 8px 22px 26px;
            background: linear-gradient(180deg, #334b54, #101c22 55%, #05090c);
            border-bottom: 2px solid rgba(79,205,230,0.45);
          }

          .bw-tanker-deck {
            position: absolute;
            left: 22px;
            bottom: 20px;
            display: flex;
            gap: 4px;
          }

          .bw-tanker-deck span {
            width: 20px;
            height: 10px;
            border-radius: 5px 5px 2px 2px;
            background: linear-gradient(180deg, #7c8e92, #253a42);
            border: 1px solid rgba(190,226,233,0.25);
          }

          .bw-tanker-bridge {
            position: absolute;
            right: 13px;
            bottom: 21px;
            width: 20px;
            height: 22px;
            background: linear-gradient(135deg, #d7e2df, #607279);
            clip-path: polygon(15% 0, 100% 20%, 100% 100%, 0 100%, 0 28%);
          }

          .bw-tanker-wake {
            position: absolute;
            right: 100%;
            bottom: 3px;
            width: 70px;
            height: 6px;
            background: linear-gradient(90deg, transparent, rgba(164,237,255,0.62));
            filter: blur(2px);
          }

          .bw-tanker-signal {
            position: absolute;
            right: 9px;
            top: -7px;
            z-index: 2;
            display: grid;
            width: 16px;
            height: 16px;
            place-items: center;
            border-radius: 999px;
            background: #ff6a1a;
            color: #fff;
            font-size: 13px;
            font-weight: 900;
            animation: bwLiveDot 1.05s ease-in-out infinite;
          }

          .bw-tanker-scanning {
            opacity: 0.68;
          }

          .bw-tanker-scanning .bw-tanker-signal {
            background: #315966;
            animation-duration: 2.4s;
          }
        `}
      </style>

      <div
        className="grid items-center gap-4 border-b pb-4 xl:grid-cols-[270px_minmax(0,1fr)_160px]"
        style={{ borderColor: C.line }}
      >
        <div>
          <div
            className="text-[9px] font-bold uppercase tracking-[0.28em]"
            style={{ color: C.dimGold }}
          >
            NEXOVATE MARKET COMMAND CENTER
          </div>

          <h1
            className="mt-1 text-2xl font-bold"
            style={{
              color: C.softGold,
              textShadow: "0 0 14px rgba(247,201,72,0.35)",
            }}
          >
            BloombergWatch
          </h1>

          <div className="mt-1 text-[11px]" style={{ color: C.gold }}>
            Global intelligence and live market monitor
          </div>
        </div>

        <MarketStrip items={marketQuotes} error={marketError} />

        <div
          className="text-right text-[9px] font-bold uppercase tracking-[0.16em]"
          style={{ color: C.gold }}
        >
          <div>Updated</div>

          <div>
            {updatedAt
              ? new Date(updatedAt).toLocaleTimeString("en-IN")
              : "--"}
          </div>

          <div style={{ color: intelligenceEnabled ? C.green : C.red }}>
            {intelligenceEnabled ? "Live" : "Paused"}
          </div>
          <div>{intelligenceEnabled ? `Auto 5s - Next ${countdown}s` : "External fetching stopped"}</div>

          <button
            type="button"
            onClick={toggleIntelligence}
            disabled={controlBusy}
            className="ml-auto mt-2 flex items-center gap-2 rounded border px-3 py-2 text-[10px] font-bold disabled:opacity-50"
            style={{
              borderColor: intelligenceEnabled ? "rgba(239,68,68,0.55)" : "rgba(34,197,94,0.55)",
              background: intelligenceEnabled ? "#3d090d" : "#07361f",
              color: intelligenceEnabled ? "#ff9aa3" : "#82f0ad",
            }}
            title={intelligenceEnabled ? "Pause news and market network requests" : "Resume news and market network requests"}
          >
            {intelligenceEnabled ? <Pause size={13} /> : <Play size={13} />}
            {controlBusy ? "Changing..." : intelligenceEnabled ? "Stop feeds" : "Start feeds"}
          </button>

          <button
            type="button"
            onClick={() => {
              if (intelligenceEnabledRef.current) {
                loadNews();
                loadMarketQuotes();
              }
            }}
            disabled={!intelligenceEnabled || controlBusy}
            className="ml-auto mt-2 flex items-center gap-2 rounded px-3 py-2 text-[10px] font-bold"
            style={{ background: C.emerald, color: "#ffffff", opacity: intelligenceEnabled ? 1 : 0.45 }}
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="border-b p-3 text-sm" style={{ borderColor: C.red, color: C.red, background: "#190407" }}>
          {error}
        </div>
      )}

      <LiveWorldMap items={items} />
    </div>
  );
}
